import {Button} from '@app/components/ui/button';
import {cn} from '@app/lib/utils';
import {AlertCircle, ArrowLeft} from 'lucide-react';
import type React from 'react';
import type { ComponentProps } from 'react';
import {useTranslation} from 'react-i18next';
import {AnyLink} from '@app/components/app-link';

export interface ErrorPageProps {
    statusCode: number;
    title: string;
    description: string;
    action?:
        | {
        label?: string;
        href: string | (() => void);
    }
        | 'refresh';
    className?: string;
    icon?: React.ReactNode | React.FC<ComponentProps<typeof AlertCircle>>;
    fullScreen?: boolean;
    hideBackButton?: boolean;
}

export function ErrorPage({
                              statusCode,
                              title,
                              description,
                              action,
                              className,
                              icon: Icon = <AlertCircle className="text-destructive h-10 w-10"/>,
                              fullScreen = true,
                              hideBackButton
                          }: ErrorPageProps) {
    const {t} = useTranslation();
    const resolvedAction = action ?? {label: t('common.go-home'), href: '/'};

    if (
        Icon
        && (
            typeof Icon === "function"
            || (
                typeof Icon === "object"
                && "render" in Icon
            )
        )
    ) {
        const IconComponent = Icon as typeof AlertCircle;
        Icon = <IconComponent className="text-destructive h-10 w-10"/>
    }

    // noinspection HtmlUnknownTarget
    return (
        <div
            className={cn("flex flex-col items-center justify-center", fullScreen && "h-[100dvh]", !fullScreen && "h-full")}>
            <div className={cn('flex max-w-md flex-col items-center justify-center text-center', className)}>
                <div className="mb-4">{Icon}</div>
                <h1 className="mb-2 text-4xl font-bold">{statusCode}</h1>
                <h2 className="mb-3 text-2xl font-semibold">{title}</h2>
                <p className="text-muted-foreground mb-6">{description}</p>
                <div className="flex gap-4">
                    {resolvedAction === 'refresh' ? (
                        <Button variant="outline" onClick={() => window.location.reload()}>
                            <ArrowLeft className="mr-2 h-4 w-4"/>
                            {t('common.buttons.refresh')}
                        </Button>
                    ) : (typeof resolvedAction.href === "function" ? (
                        <Button variant="outline" onClick={() => (resolvedAction.href as (() => void) | undefined)?.()}>
                            {resolvedAction.label ?? t('common.ok')}
                        </Button>
                    ) : (
                        <Button asChild>
                            <AnyLink href={resolvedAction.href}>{resolvedAction.label ?? t('common.ok')}</AnyLink>
                        </Button>
                    ))}
                    {!hideBackButton && (
                        <Button variant="outline" onClick={() => window.history.back()}>
                            <ArrowLeft className="mr-2 h-4 w-4"/>
                            {t('common.buttons.go-back')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
